import { DragAndDropFile, Dialog } from '@phi/components';
import { defineComponent, h, PropType } from 'vue';
import { Option } from '@vaaas/fpts/option';
import Tabs from '../Tabs';
import { has_selected_document, is_connected_to_onshape, State } from '../../state';
import LoadDefaultObject from '../LoadDefaultObject';
import { Length } from '../../Units';
import { loadFileAsText, loadGivenModelString, loadPhiModelFromOnshape } from '../../input_output';
import { listDocumentElements } from '@phi/frontend/api/calls';
import { OnshapeElement } from '@phi/onshape';
import { SelectDocumentElement } from '@components/OnshapeExplorer';
import { uid_scene_is_empty } from '../../ui_display';
import DefaultModelData from '../../DefaultModelData';
import { SessionType } from '@phi/authentication/frontend';
import { px } from '@phi/util/css';

type Tab = 'default-object'
    | 'phi-from-cloud'
    | 'phi-file';

export default defineComponent({
    props: {
        show: { required: true, type: Boolean},
        framework: {
            required: true,
            type: null as any as PropType<State['framework']>,
        },
        units: {
            required: true,
            type: String as PropType<Length>,
        },
        state: { required: true, type: Object as PropType<State> },
        session_type: { required: true, type: null as any as PropType<Set<SessionType>> },
    },

    emits: {
        'update:show': (x: boolean) => true,
    },

    data() {
        return {
            existing_documents: [] as OnshapeElement[],
            local_file: undefined as Option<File>,
            default_object: undefined as Option<Parameters<typeof loadGivenModelString>[0]>,
            onshape_file: undefined as Option<OnshapeElement>,
            tab: 'default-object' as Tab,
            load_into: false,
            had_selected_document: has_selected_document(),
        };
    },

    watch: {
        async show(value: typeof this.show) {
            if (value) {
                loading_into_current_model = this.load_into = !uid_scene_is_empty();
                global_default_model_data = new DefaultModelData();
                this.existing_documents = await this.fetch_documents();
                this.had_selected_document = has_selected_document();
            } else
                setTimeout(this.reset, 500);
        },

        units(value: typeof this.units) {
            const elem = document.querySelector('#selectModelModalWorkspaceUnitDropdown') as HTMLSelectElement | undefined;
            if (!elem) return;
            elem.value = value;
        },
    },

    methods: {
        async fetch_documents() {
            if (
                this.state.framework !== 'onshape'
                || !this.state.document
                || !this.state.workspace
            )
                return [];
            const response = await listDocumentElements(this.state.document, this.state.workspace, true);
            if (response instanceof Error)
                return [];
            return response.filter(x => x.elementType === 'BLOB' && x.name.endsWith('.phi'));
        },

        async onLoad() {
            if (!this.should_show_load) return;
            else if (this.default_object && this.tab === 'default-object')
                loadGivenModelString(this.default_object, this.should_create_document);
            else if (this.onshape_file && this.tab === 'phi-from-cloud')
                loadPhiModelFromOnshape(this.onshape_file.id, this.onshape_file.name);
            else if (this.local_file && this.tab === 'phi-file')
                loadFileAsText(this.local_file);
            this.$emit('update:show', false);
        },

        reset() {
            this.local_file = undefined;
            this.default_object = undefined;
            this.onshape_file = undefined;
        },
    },

    computed: {
        tabs() {
            const xs = [];
            xs.push({
                id: 'default-object',
                text: 'Default object',
                slot: () => h(LoadDefaultObject, {
                    defaultUnit: this.units,
                    value: this.default_object,
                    'onUpdate:value': (x) => this.default_object = x,
                    load_into: this.load_into,
                    standalone:
                        this.state.framework === undefined
                        && this.session_type.has('onshape'),
                    document: this.state.document,
                    'onUpdate:document': x => this.state.document = x,
                    workspace: this.state.workspace,
                    'onUpdate:workspace': x => this.state.workspace = x,
                    had_selected_document: this.had_selected_document,
                }),
            });

            if (is_connected_to_onshape())
                xs.push({
                    id: 'phi-from-cloud',
                    text: 'Phi model from Cloud',
                    slot: () => h('div', { class: 'select-onshape-file' }, [
                        h('p', 'Load a saved Phi model from Onshape'),

                        h(SelectDocumentElement, {
                            document: this.state.document,
                            workspace: this.state.workspace,
                            element: this.onshape_file,
                            elementFilter: (x) => x.elementType === 'BLOB' && x.name.endsWith('.phi'),
                            'onUpdate:element': x => this.onshape_file = x,
                            'onUpdate:document': x => this.state.document = x,
                            'onUpdate:workspace': x => this.state.workspace = x,
                            can_select_document: this.framework === undefined,
                        }),
                    ]),
                });

            xs.push({
                id: 'phi-file',
                text: 'Phi model from file',
                slot: () => h(DragAndDropFile, {
                    type: '.phi',
                    label: 'Drag & drop a .phi file from your computer',
                    value: this.local_file,
                    'onUpdate:value': (x) => this.local_file = x,
                }),
            });

            return xs;
        },

        should_show_load() {
            return (
                (
                    this.default_object
                    && this.default_object.file_name.trim().length
                    && this.tab === 'default-object'
                )
                || (this.onshape_file && this.tab === 'phi-from-cloud')
                || (this.local_file && this.tab === 'phi-file')
            );
        },

        should_create_document() {
            return !this.load_into
                && !this.state.document
                && !this.state.workspace
                && this.default_object
                && this.tab === 'default-object'
                && is_connected_to_onshape();
        },
    },

    render() {
        return h(Dialog, {
            show: this.show,
            'onUpdate:show': x => this.$emit('update:show', x),
            id: 'selectModelModal',
            title: this.load_into
                ? 'Pick a model to add to workspace'
                : 'Pick a model to begin with',
            width: 'wide',
            backdrop: false,
            style: {
                position: 'relative',
                left: px(this.state.aside ? -560/2 : 0)
            },
            unload: true,
            confirm_enabled: this.should_show_load,
            confirm_label: 'Load',
            onClose: () => this.$emit('update:show', false),
            onConfirm: this.onLoad,
        }, () => h(Tabs, {
            tabs: this.tabs,
            tab: this.tab,
            'onUpdate:tab': x => this.tab = x as Tab,
        }));
    }
});
